import type { ProcessorId } from '../domain/tools/types';
import type { ProcessingRequest, ProcessingResult } from './contracts';
import { ProcessingCancelledError, ProcessingError } from './errors';
import {
  ProcessingWorkerClient,
  type ProcessingWorkerTransport,
  type WorkerRunOptions,
} from '../workers/client';

export type ProcessingWorkerFactory = (
  processorId: ProcessorId,
) => ProcessingWorkerTransport;

interface ActiveJob {
  readonly controller: AbortController;
  readonly client: ProcessingWorkerClient;
  readonly transport: ProcessingWorkerTransport;
}

export class ProcessingJobRunner {
  private readonly activeJobs = new Map<string, ActiveJob>();

  public constructor(private readonly createWorker: ProcessingWorkerFactory) {}

  public async run(
    request: ProcessingRequest,
    options: WorkerRunOptions = {},
  ): Promise<ProcessingResult> {
    if (this.activeJobs.has(request.jobId)) {
      throw new ProcessingError('processing.duplicateJob', { jobId: request.jobId });
    }
    if (options.signal?.aborted === true) throw new ProcessingCancelledError(false);

    const controller = new AbortController();
    const relayAbort = () => controller.abort();
    options.signal?.addEventListener('abort', relayAbort, { once: true });

    const transport = this.createWorker(request.processorId);
    const client = new ProcessingWorkerClient(transport);
    this.activeJobs.set(request.jobId, { controller, client, transport });

    try {
      return await client.run(request, { ...options, signal: controller.signal });
    } finally {
      options.signal?.removeEventListener('abort', relayAbort);
      this.activeJobs.delete(request.jobId);
      transport.terminate();
    }
  }

  public cancel(jobId: string): boolean {
    const active = this.activeJobs.get(jobId);
    if (active === undefined) return false;
    active.controller.abort();
    return true;
  }

  public hardTerminate(jobId: string): boolean {
    const active = this.activeJobs.get(jobId);
    if (active === undefined) return false;
    active.client.hardTerminate();
    return true;
  }

  public dispose(): void {
    for (const active of this.activeJobs.values()) active.client.hardTerminate();
    this.activeJobs.clear();
  }
}

